import React, { useCallback, useEffect, useMemo } from 'react';
import { Flex, Text, Menu, MenuItemProps, Link, Table, TableContainer, Tbody, Td, Thead, Tr } from '@televet/kibble-ui';
import { CardBrand } from 'shared/types/graphql';
import CardTypeRow from './CardTypeRow';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { addSelectedCardBrand, setPaymentTypesInitialized, setPimsPaymentType } from '../../state/paymentSettingsSlice';
import { InfoIcon } from 'shared/components';
import { IntegrationSystemDisplayNameType, useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { getZendeskLink } from '../../utils';
import { useTracking } from 'shared/providers/TrackingProvider';
import { SettingsPaymentsEvents } from 'shared/providers/TrackingProvider/EventNames';

interface ICardTypeTableProps {
  paymentTypes: MenuItemProps[];
  setHasUnsavedChange: React.Dispatch<React.SetStateAction<boolean>>;
}

const CardTypeTable = ({ paymentTypes, setHasUnsavedChange }: ICardTypeTableProps): JSX.Element => {
  const { primaryIntegrationName } = useIntegrationsProvider();
  const dispatch = useAppDispatch();
  const selectedCardBrands = useAppSelector((state) => state.paymentSettings.selectedCardBrands);
  const paymentTypesInitialized = useAppSelector((state) => state.paymentSettings.paymentTypesInitialized);
  const { trackEvent } = useTracking();

  // sort alphabetically with Other at the end
  const sortedCardBrands = useMemo(() => {
    const sorted = selectedCardBrands.filter((brand) => brand !== CardBrand.Unknown).sort();
    sorted.push(CardBrand.Unknown);

    return sorted;
  }, [selectedCardBrands]);

  const findMatchingPaymentType = useCallback(
    (keys: string[]): string | undefined => {
      let match = undefined;
      for (let i = 0; i < keys.length; i++) {
        match = paymentTypes?.find((type) => (type.label as string)?.toLowerCase().includes(keys[i]))?.value;
      }

      return match;
    },
    [paymentTypes],
  );

  // predict selections based on pims card type descriptions
  // only do on initial load
  useEffect(() => {
    if (paymentTypesInitialized) return;

    const amexType = findMatchingPaymentType(['amex', 'american express']);
    const discoverType = findMatchingPaymentType(['discover']);
    const mastercardType = findMatchingPaymentType(['mc', 'mastercard']);
    const visaType = findMatchingPaymentType(['visa']);

    if (amexType && !selectedCardBrands.includes(CardBrand.Amex)) {
      dispatch(setPimsPaymentType({ cardBrand: CardBrand.Amex, paymentTypeId: amexType }));
      dispatch(addSelectedCardBrand(CardBrand.Amex));
    }

    if (discoverType && !selectedCardBrands.includes(CardBrand.Discover)) {
      dispatch(setPimsPaymentType({ cardBrand: CardBrand.Discover, paymentTypeId: discoverType }));
      dispatch(addSelectedCardBrand(CardBrand.Discover));
    }

    if (mastercardType && !selectedCardBrands.includes(CardBrand.Mastercard)) {
      dispatch(setPimsPaymentType({ cardBrand: CardBrand.Mastercard, paymentTypeId: mastercardType }));
      dispatch(addSelectedCardBrand(CardBrand.Mastercard));
    }

    if (visaType && !selectedCardBrands.includes(CardBrand.Visa)) {
      dispatch(setPimsPaymentType({ cardBrand: CardBrand.Visa, paymentTypeId: visaType }));
      dispatch(addSelectedCardBrand(CardBrand.Visa));
    }

    dispatch(setPaymentTypesInitialized(true));
  }, [dispatch, findMatchingPaymentType, selectedCardBrands, paymentTypesInitialized]);

  const cardTypeOptions: { label: CardBrand; value: CardBrand }[] = useMemo(() => {
    return [
      {
        label: CardBrand.Amex,
        value: CardBrand.Amex,
      },
      {
        label: CardBrand.Discover,
        value: CardBrand.Discover,
      },
      {
        label: CardBrand.Mastercard,
        value: CardBrand.Mastercard,
      },
      {
        label: CardBrand.Visa,
        value: CardBrand.Visa,
      },
    ];
  }, []);

  const availableCardTypeOptions = useMemo(() => {
    return cardTypeOptions.filter((option) => !selectedCardBrands.includes(option.value));
  }, [selectedCardBrands, cardTypeOptions]);

  const handleAddCardType = useCallback(
    (selectedOption) => {
      dispatch(addSelectedCardBrand(selectedOption.value));
    },
    [dispatch],
  );

  return (
    <TableContainer mt={4} overflowY="visible" overflowX="visible">
      <Table variant="striped">
        <Thead>
          <Tr>
            <Td px={2} w="35%">
              <Text size="xs">CARD TYPE</Text>
            </Td>
            <Td px={2} w="50%">
              <Flex align="center">
                <Text size="xs" ml={3}>{`PAYMENT TYPE (${primaryIntegrationName})`}</Text>
                <InfoIcon heading="Payment Type">
                  <Text size="sm" variant="onContrast">
                    Choose the Payment Type in {primaryIntegrationName} that you would like to appear for each Card
                    Type.
                  </Text>
                  <Link
                    mt={2}
                    iconProps={{ name: 'externalLink' }}
                    isExternal
                    href={getZendeskLink(primaryIntegrationName as IntegrationSystemDisplayNameType)}
                    onClick={(): void => trackEvent(SettingsPaymentsEvents.PaymentTypeAddition)}
                    size="sm"
                    variant="interactiveOnContrast"
                  >
                    {`How to add a Payment Type in ${primaryIntegrationName}`}
                  </Link>
                </InfoIcon>
              </Flex>
            </Td>
          </Tr>
        </Thead>
        <Tbody>
          {sortedCardBrands.map((brand): JSX.Element => {
            return (
              <CardTypeRow
                key={`card-type-row-${brand}`}
                cardBrand={brand}
                paymentTypes={paymentTypes}
                setHasUnsavedChange={setHasUnsavedChange}
              />
            );
          })}
        </Tbody>
      </Table>
      {availableCardTypeOptions.length > 0 && (
        <Flex>
          <Menu
            buttonProps={{
              leftIconName: 'plus',
              showRightIcon: false,
              size: 'sm',
              w: 'fit-content',
              text: 'Add Card Type',
              variant: 'ghost',
              mt: 4,
            }}
            listProps={{ menuItems: availableCardTypeOptions, onSelect: handleAddCardType }}
          />
        </Flex>
      )}
    </TableContainer>
  );
};

export default CardTypeTable;
